import React from 'react'

// Reintroduce imports and cache creation
import { ScriptCache } from './lib/ScriptCache'
import GoogleApi from './lib/GoogleApi'

const serialize = obj => JSON.stringify(obj)
const isSame = (obj1, obj2) =>
  obj1 === obj2 || serialize(obj1) === serialize(obj2)

const defaultCreateCache = options => {
  const apiKey = options.apiKey
  const libraries = options.libraries || ['places']
  const version = options.version || '3.31'
  const language = options.language || 'en'
  const url = options.url
  const client = options.client
  const region = options.region

  return ScriptCache({
    google: GoogleApi({
      apiKey: apiKey,
      language: language,
      libraries: libraries,
      version: version,
      url: url,
      client: client,
      region: region
    })
  })
}

const DefaultLoadingContainer = props => <div>Loading...</div>

export const GoogleApiWrapper = input => ActualWrappedComponent => {
  console.log('GoogleApiWrapper Factory - Input:', input);
  console.log('GoogleApiWrapper Factory - ActualWrappedComponent:', ActualWrappedComponent);

  // Allow function definitions for cache creation
  const createCache = input.createCache || defaultCreateCache

  if (!ActualWrappedComponent) {
    console.error("GoogleApiWrapper Factory received undefined component!");
    return () => <div>Error: Component provided to GoogleApiWrapper was undefined.</div>;
  }

  // Define scriptCache instance at the HOC level
  const scriptCache = createCache(typeof input === 'function' ? {} : input) // Initial options might be empty if input is a function

  class Wrapper extends React.Component {
    constructor(props) {
      super(props);

      // Calculate initial options based on props if input is a function
      const options = typeof input === 'function' ? input(this.props) : input;

      this.state = {
        loaded: false,
        google: null,
        options: options, // Use calculated options
        error: null
      }
      this.LoadingContainer = input.LoadingContainer || DefaultLoadingContainer;
    }

    // Reintroduce lifecycle methods (now calling initialize)
    // static getDerivedStateFromProps(...) { ... } // Keep commented for now
    componentDidMount() {
      // Now call initialize
      this.initialize()
    }

    componentDidUpdate(prevProps) {
      const prevOptions = typeof input === 'function' ? input(prevProps) : input
      const currentOptions = typeof input === 'function' ? input(this.props) : input

      if (isSame(prevOptions, currentOptions)) {
        return;
      }

      // Re-initialize if options change
      this.initialize()
    }

    // onLoad method (unchanged, called by script cache)
    onLoad = (google, error) => {
      if (error) {
        this.setState({ error: error, loaded: true })
      } else {
        this.setState({ loaded: true, google })
      }
    }

    // Reintroduce initialize method definition
    initialize() {
      const options = Object.assign({}, this.state.options) // Use options from state

      // Reset state if needed
      if (this.state.loaded) {
        this.setState({ loaded: false, google: null, error: null });
      }

      // Correctly use the onLoad mechanism for the 'google' script
      scriptCache.google.onLoad((err, result) => { 
        if (err) {
          console.error('Wrapper initialize - google script failed to load', err);
          this.onLoad(null, err); // Call Wrapper's onLoad with error
        } else {
          // Check if window.google is available
          if (window.google) {
             this.onLoad(window.google); // Call Wrapper's onLoad with the global google object
          } else {
             console.error('Wrapper initialize - google script loaded but window.google is not defined!');
             this.onLoad(null, new Error('Google script loaded but window.google is missing.'));
          }
        }
      });
    }

    render() {
      const { LoadingContainer } = this

      if (this.state.error) {
        console.error("GoogleApiWrapper rendering error state:", this.state.error);
        return <div>Google Maps API failed to load. Please refresh the page.</div>;
      }

      if (!this.state.loaded) {
        return <LoadingContainer />;
      }

      const props = Object.assign({}, this.props, {
        loaded: this.state.loaded,
        google: this.state.google
      })

      if (!ActualWrappedComponent) {
        console.error("Wrapper render - ActualWrappedComponent became undefined before final render!");
        return <div>Error: Wrapped component is missing in render.</div>;
      }

      return <ActualWrappedComponent {...props} />;
    }
  }

  const wrappedComponentName = ActualWrappedComponent.displayName || ActualWrappedComponent.name || 'Component';
  Wrapper.displayName = `GoogleApiWrapper(${wrappedComponentName})`;

  console.log(`GoogleApiWrapper Factory - Returning Wrapper component for ${wrappedComponentName}`);
  return Wrapper;
}

export default GoogleApiWrapper
